import { useEffect, useMemo, useState } from 'react';
import { BrandMark, StatusPill } from '@msarat/theme/components';

import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.jsx';
import { PARENT_SECTIONS, queryParam } from '../lib/router.js';
import { Curriculum } from './Curriculum.jsx';
import { Students } from './Students.jsx';

/**
 * Parent dashboard.
 *
 * The layout mirrors the preview on the marketing site — sidebar, summary
 * tiles, skill progress, next lesson — because a parent should land on the
 * thing they were shown. What differs is honesty: none of that data exists
 * yet, so every panel states what it is waiting for rather than showing
 * invented numbers.
 *
 * A family may have no children yet, one, or several. All three are ordinary:
 * the first is the state every new account starts in, and the last is the whole
 * reason a parent account exists rather than a student one.
 */
export function Dashboard({ user, section, search, onNavigate, onSignOut }) {
  const { t } = useI18n();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  // An unverified parent may look around but cannot upload a curriculum. The
  // API is the authority; the UI explains, so the block is never a surprise.
  const active = Boolean(user?.email_verified);

  async function refreshStudents() {
    setLoading(true);
    try {
      const result = await api.listStudents();
      setStudents(result.items ?? []);
    } catch {
      setStudents([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refreshStudents();
  }, []);

  // The selected child lives in the query string, so a parent can bookmark or
  // share a view of one child, and a refresh keeps them there.
  const requestedId = queryParam(search, 'student');
  const selected = useMemo(() => {
    if (students.length === 0) return null;
    return students.find((student) => student.id === requestedId) ?? students[0];
  }, [students, requestedId]);

  function selectStudent(id) {
    const current = PARENT_SECTIONS.find((item) => item.id === section)?.path ?? '/';
    onNavigate(`${current}?student=${encodeURIComponent(id)}`);
  }

  return (
    <div className="dashboard" data-surface="parent">
      <div className="dashboard__panel">
        <aside className="dashboard__sidebar">
          <span className="brand">
            <BrandMark />
            <span>{t('brand.name')}</span>
          </span>

          <nav className="dashboard__nav" aria-label={t('nav.sections')}>
            {PARENT_SECTIONS.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => onNavigate(item.path)}
                aria-current={section === item.id ? 'page' : undefined}
              >
                {t(`nav.${item.id}`)}
              </button>
            ))}
          </nav>

          <LanguageSwitcher />

          <div className="dashboard__account">
            <span className="dashboard__avatar" aria-hidden="true">
              {(user?.name || '؟').trim().charAt(0)}
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700 }}>{user?.name}</div>
              <button type="button" onClick={onSignOut} className="linklike">
                {t('common.signOut')}
              </button>
            </div>
          </div>
        </aside>

        <main className="dashboard__main">
          {!active ? <VerificationNotice email={user?.email} /> : null}

          {section === 'overview' ? (
            <Overview
              students={students}
              selected={selected}
              loading={loading}
              onSelectStudent={selectStudent}
              onAddStudent={() => onNavigate('/students')}
            />
          ) : null}

          {section === 'students' ? (
            <Students students={students} loading={loading} onChanged={refreshStudents} />
          ) : null}

          {section === 'lessons' ? (
            <Placeholder
              title={t('nav.lessons')}
              body={t('lessons.body')}
            />
          ) : null}

          {section === 'skills' ? (
            <Placeholder
              title={t('nav.skills')}
              body={t('skills.sectionBody')}
            />
          ) : null}

          {section === 'curriculum' ? <Curriculum active={active} students={students} /> : null}
        </main>
      </div>
    </div>
  );
}

/**
 * The account-not-active notice.
 *
 * Explicit about the consequence, not just the state: "your account is not
 * active" alone leaves a parent guessing why an upload button does nothing.
 */
function VerificationNotice({ email }) {
  const { t } = useI18n();
  return (
    <div className="alert alert--error" role="status">
      <strong>{t('notice.inactiveTitle')}</strong> {t('notice.inactiveBody', { email })}
    </div>
  );
}

/**
 * Language switcher.
 *
 * Placed in the sidebar rather than buried in settings: a parent who lands in
 * the wrong language needs it before they can find anything else.
 */
function LanguageSwitcher() {
  const { t, language, setLanguage } = useI18n();
  const options = [
    { value: 'ar', label: t('common.arabic') },
    { value: 'en', label: t('common.english') },
  ];

  return (
    <div className="switcher">
      <span className="field__label" id="language-label">
        {t('common.language')}
      </span>
      <div className="switcher__options" role="group" aria-labelledby="language-label">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            lang={option.value}
            className={`chip${language === option.value ? ' chip--active' : ''}`}
            aria-pressed={language === option.value}
            onClick={() => setLanguage(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Overview. Three shapes: no children, one child, several. */
function Overview({ students, selected, loading, onSelectStudent, onAddStudent }) {
  const { t } = useI18n();

  if (loading) {
    return (
      <p className="empty" aria-live="polite">
        {t('common.loading')}
      </p>
    );
  }

  // No children yet is the state every new account starts in, so it gets a real
  // first step rather than an apology for being empty.
  if (students.length === 0) {
    return (
      <>
        <header className="dashboard__head">
          <div>
            <p className="eyebrow">{t('overview.welcome')}</p>
            <h1>{t('overview.emptyTitle')}</h1>
          </div>
        </header>

        <section className="panel">
          <p className="empty">{t('overview.emptyBody')}</p>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button className="button button--primary" type="button" onClick={onAddStudent}>
              {t('students.add')}
            </button>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <header className="dashboard__head">
        <div>
          <p className="eyebrow">{t('overview.welcome')}</p>
          <h1>{t('overview.today', { name: selected?.name })}</h1>
        </div>
      </header>

      {/* The switcher appears only when there is a choice to make. */}
      {students.length > 1 ? (
        <div className="switcher">
          <span className="field__label" id="student-switch-label">
            {t('switcher.student')}
          </span>
          <div
            className="switcher__options"
            role="group"
            aria-labelledby="student-switch-label"
          >
            {students.map((student) => (
              <button
                key={student.id}
                type="button"
                className={`chip${student.id === selected?.id ? ' chip--active' : ''}`}
                aria-pressed={student.id === selected?.id}
                onClick={() => onSelectStudent(student.id)}
              >
                {student.name}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <section className="tiles" aria-label={t('overview.summaryFor', { name: selected?.name ?? '' })}>
        <Tile icon="✓" tone="success" label={t('tiles.time')} value={t('tiles.none')} />
        <Tile icon="◎" tone="review" label={t('tiles.correct')} value={t('tiles.none')} />
        <Tile icon="↗" tone="assisted" label={t('tiles.independent')} value={t('tiles.none')} />
      </section>

      <div className="panels panels--split">
        <section className="panel" aria-labelledby="skills-title">
          <div className="panel__head">
            <h2 id="skills-title" style={{ fontSize: 'var(--text-lg)' }}>
              {t('skills.progress')}
            </h2>
          </div>

          <p className="empty">{t('skills.empty', { name: selected?.name })}</p>

          {/* The legend stays visible while empty: it is the clearest way to show
              that assisted success and independent mastery are recorded
              differently, which is the product's core promise. */}
          <div
            style={{
              display: 'flex',
              gap: 'var(--space-2)',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <StatusPill kind="mastered" label={t('status.mastered')} />
            <StatusPill kind="assisted" label={t('status.assisted')} />
            <StatusPill kind="review" label={t('status.review')} />
          </div>
        </section>

        <section className="panel" aria-labelledby="next-title">
          <div className="panel__head">
            <h2 id="next-title" style={{ fontSize: 'var(--text-lg)' }}>
              {t('next.title')}
            </h2>
          </div>
          <p className="empty">{t('next.empty', { name: selected?.name })}</p>
        </section>
      </div>
    </>
  );
}

function Tile({ icon, tone, label, value }) {
  return (
    <div className="tile">
      <span className={`tile__icon tile__icon--${tone}`} aria-hidden="true">
        {icon}
      </span>
      <div>
        <div className="tile__label">{label}</div>
        <div className="tile__value">{value}</div>
      </div>
    </div>
  );
}

function Placeholder({ title, body }) {
  return (
    <section className="panel">
      <h2 style={{ fontSize: 'var(--text-xl)' }}>{title}</h2>
      <p className="empty">{body}</p>
    </section>
  );
}
